import { useEffect, useState } from 'react';
import { FaSpotify } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import H1 from './h1';
import Spacer from './spacer';
import AppState from '../store/appstate';
import { getArtistMeta, getUserMeta } from '../api/queries';
import ApiErrorMessage from './apiErrorMessage';
import { updateArtistAccountMeta } from '../api/mutations';
import Select from './select';
import RebrandButtonLink from './rebrandButtonLink';
import RebrandPrimaryButton from './rebrandPrimaryButton';
import useSiteConfigAuth from '../api/sanity/queries/getSiteConfigWithCode';

function SocialSpotify({ closeModal }) {
  const { t } = useTranslation('songs');
  const selectedAccount = AppState.useState((s) => s.selectedAccount);
  const artistMeta = getArtistMeta({ aac: selectedAccount });
  const userMeta = getUserMeta();
  const accountMetaMutation = updateArtistAccountMeta();
  const { data: siteConfig } = useSiteConfigAuth();
  const isTunecore = siteConfig?.display_name === 'TuneCore';

  const [apiToken, setAPIToken] = useState(null);
  const [apiError, setApiError] = useState(null);
  const [pending, setPending] = useState(false);

  const [result, setResult] = useState(null);

  const getSpotifyAPIToken = async () => {
    const apiUrl = AppState.getRawState()?.apiUrl;

    const response = await fetch(`${apiUrl}/spotify-token`);
    const responseData = await response.json();
    return responseData.access_token;
  };

  let title = 'Plug into your Spotify account';
  let desc = 'If you have a Spotify profile then connect below to help us find your music.';
  switch (userMeta?.data?.role) {
    case 'Label':
    case 'Manager':
      title = 'Plug into your artists spotify account';
      desc = 'If they have a Spotify profile then connect below to help us find their music.';
      break;
    default:
      break;
  }

  useEffect(() => {
    async function go() {
      setAPIToken(await getSpotifyAPIToken());
    }
    go();
  }, []);

  const addSpotify = () => {
    setPending(true);

    const data = {
      account_id: selectedAccount,
      facebook_obj: artistMeta.data?.facebook,
      spotify_obj: result,
      songkick_obj: artistMeta.data?.facebook?.songkick,
      twitter_obj: artistMeta.data?.facebook?.twitter,
      bandsintown_obj: artistMeta.data?.facebook?.bandsintown,
      instagram_obj: artistMeta.data?.facebook?.instagram,
    };

    accountMetaMutation.reset();

    accountMetaMutation.mutate(data, {
      onSuccess: () => {
        artistMeta.refetch();
        setPending(false);
        closeModal();
      },
      onError: (error) => {
        setApiError(error.message);
        setPending(false);
      },
    });
  };

  const getOptions = (query) => new Promise((resolve, reject) => {
      if (!query) {
        resolve([]);
        return;
      }
      fetch(
        `https://api.spotify.com/v1/search?q=${query}&type=artist&market=GB&limit=50`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${apiToken}`,
          },
        },
      )
        .then((response) => response.json())
        .then(({ artists }) => {
          resolve(
            artists.items.map((artist) => ({
              value: JSON.stringify(artist),
              name: artist.name,
              photo:
                artist.images && artist.images.length > 0
                  ? artist.images[0].url
                  : null,
            })),
          );
        })
        .catch(reject);
    });

  return (
    <div className="flex items-center flex-col p-4">
      <p className="text-6xl">
        <FaSpotify />
      </p>
      <Spacer />
      {!result && (
        <>
          <H1>{title}</H1>
          <Spacer thin />
          <p>{desc}</p>
          <Spacer />
          <div className="w-full flex flex-row items-start justify-center text-old-sentric-dark-gray">
            <Select
              debounce={500}
              className='w-full text-old-sentric-dark-gray select-input-grey-bg'
              onChange={(artist) => {
                setResult(artist);
              }}
              getOptions={getOptions}
              placeholder="Search for your Spotify profile"
              search
            />
          </div>
        </>
      )}
      {!!result && (
        <>
          <H1>Selected Spotify profile</H1>
          <Spacer />
          <div className={`${isTunecore ? 'bg-tunecore-grey-element' : 'bg-white text-old-sentric-dark-gray '} rounded-lg shadow-lg flex flex-col p-4 px-6 items-center`}>
            {JSON.parse(result).images
              && JSON.parse(result).images.length > 0 && (
                <img
                  className="w-20 h-20 object-cover rounded-full mb-4"
                  alt={JSON.parse(result).name}
                  src={JSON.parse(result).images[0].url}
                />
              )}
            {(!JSON.parse(result).images
              || JSON.parse(result).images.length === 0) && (
              <div className="w-20 h-20 bg-gray-300 rounded-full mb-4" />
            )}
            <p className="font-bold mb-4">{JSON.parse(result).name}</p>
              <RebrandButtonLink
                smallfont
                onClick={() => {
                  setResult(null);
                }}
              >
                Remove
              </RebrandButtonLink>
          </div>
        </>
      )}
      <Spacer />
      <ApiErrorMessage apiError={apiError} />
      <div className="w-full flex flex-col sm:flex-row justify-between items-center gap-3">
        <RebrandPrimaryButton
          disabled={pending || !result}
          pending={pending}
          id="btnRecordingFormAddRecording"
          onClick={addSpotify}
          arrow={!isTunecore}
        >
          Link Account
        </RebrandPrimaryButton>

        <RebrandButtonLink
          onClick={closeModal}
        >
          {t('songs:cancel-and-close')}
        </RebrandButtonLink>
      </div>
    </div>
  );
}

export default SocialSpotify;
